import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import services from "../data/services";

function SearchInput({ value, onChange, onSearch, className = "w-full" }) {
  const [query, setQuery] = useState(value || "");
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);
  const navigate = useNavigate();

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filtered = query.trim().length > 0
    ? services.filter((s) =>
        s.title.toLowerCase().includes(query.toLowerCase()) ||
        s.category.toLowerCase().includes(query.toLowerCase())
      )
    : [];

  const handleChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    setOpen(val.trim().length > 0);
    onChange?.(e);
  };

  const handleSelect = (service) => {
    setQuery(service.title);
    setOpen(false);
    navigate(`/services/${service.id}/booking`);
  };

  const handleSearch = () => {
    setOpen(false);
    onSearch?.();
    if (query.trim()) {
      navigate(`/search?q=${encodeURIComponent(query.trim())}`);
    } else {
      navigate("/services");
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Escape") setOpen(false);
    if (e.key === "Enter") handleSearch();
  };

  return (
    <div ref={wrapperRef} className={`relative min-w-0 ${className}`}>
      {/* Input bar */}
      <div className="flex items-center bg-white border border-gray-200 rounded-2xl px-2 py-2 gap-2 shadow-sm w-full min-w-0">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={1.5}
          stroke="currentColor"
          className="w-4 h-4 text-gray-400 ml-2 flex-shrink-0"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
        </svg>
        <input
          type="text"
          value={query}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onFocus={() => query.trim().length > 0 && setOpen(true)}
          placeholder="Search service"
          className="flex-1 outline-none font-manrope-regular text-base text-gray-700 placeholder:text-gray-400 bg-transparent"
        />
        {query && (
          <button
            onClick={() => { setQuery(""); setOpen(false); }}
            className="text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
        <button
          onClick={handleSearch}
          className="bg-primary text-white font-manrope-semibold text-sm sm:text-base px-4 sm:px-6 py-2 rounded-xl hover:opacity-90 transition-opacity flex-shrink-0"
        >
          Search
        </button>
      </div>

      {/* Dropdown results */}
      {open && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-2xl shadow-xl border border-gray-100 z-50 max-h-80 overflow-y-auto">
          {filtered.length > 0 ? (
            <>
              <div className="px-4 pt-3 pb-1">
                <span className="font-manrope-medium text-xs text-secondary">
                  {filtered.length} result{filtered.length > 1 ? "s" : ""} for "{query}"
                </span>
              </div>
              <ul>
                {filtered.map((service, i) => (
                  <li key={service.id}>
                    <button
                      onClick={() => handleSelect(service)}
                      className="w-full flex items-center gap-3 px-4 py-3 hover:bg-bg transition-colors text-left"
                    >
                      {/* Thumbnail */}
                      <img
                        src={service.image}
                        alt={service.title}
                        className="w-10 h-10 rounded-xl object-cover flex-shrink-0"
                      />
                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <p className="font-manrope-semibold text-sm text-black truncate">
                          {service.title}
                        </p>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="font-manrope-regular text-xs text-secondary">{service.category}</span>
                          <span className="text-gray-300">•</span>
                          <span className="font-manrope-regular text-xs text-secondary">{service.duration}</span>
                        </div>
                      </div>
                      {/* Price */}
                      <span className="font-manrope-bold text-sm text-black flex-shrink-0">${service.price}</span>
                    </button>
                    {i < filtered.length - 1 && (
                      <div className="mx-4 border-t border-gray-50" />
                    )}
                  </li>
                ))}
              </ul>
              <div className="px-4 py-3 border-t border-gray-100">
                <button
                  onClick={() => { setOpen(false); navigate("/services"); }}
                  className="w-full text-center font-manrope-medium text-sm text-primary hover:opacity-80 transition-opacity"
                >
                  View all services →
                </button>
              </div>
            </>
          ) : (
            <div className="px-4 py-6 flex flex-col items-center gap-2">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-8 h-8 text-gray-300">
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
              </svg>
              <p className="font-manrope-semibold text-sm text-secondary">No services found</p>
              <p className="font-manrope-regular text-xs text-gray-400">Try a different keyword</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default SearchInput;
